import { authService } from '@innovamat/radiance-utils';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import type {
  LoginHookProps,
  LoginResponse,
  RosterStreamProvider,
} from '../types/login';
import { AuthError } from '../utils/AuthError';

export function useRosterStreamLogin({
  dispatch,
  autologin,
  provider,
  rosterStreamAppId,
}: LoginHookProps & { rosterStreamAppId: string }) {
  const { mutate } = useMutation<LoginResponse, AuthError, string>({
    mutationFn: async (code: string) => {
      return await authService.rosterStreamLogin(code);
    },
  });

  const handleSetLoading = (loading: boolean) => {
    dispatch({
      type: 'SET_IS_LOADING',
      payload: {
        loading,
        key: { type: 'roster_stream' },
      },
    });
  };

  const handleError = (detail: string) => {
    const error = new AuthError({
      title: 'roster_stream_error',
      detail,
    });
    dispatch({ type: 'SET_ERROR', payload: error });
    handleSetLoading(false);
  };

  const handleLogin = (provider: RosterStreamProvider) => {
    const callback = `${window.location.origin}${window.location.search}`;
    const url = `https://sso.us-east-1.rosterstream.com/login/${provider}/${rosterStreamAppId}?callback=${encodeURIComponent(
      callback
    )}`;
    window.location.href = url;
  };

  const handleCodeLogin = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (!code) return;
    handleSetLoading(true);
    mutate(code, {
      onSuccess(tokens) {
        dispatch({
          type: 'SET_LOGIN_INFO',
          payload: {
            response: tokens,
            loggedFrom: { type: 'roster_stream' },
          },
        });
        handleSetLoading(false);
        searchParams.delete('code');
      },
      onError(error) {
        handleError(error.detail || 'RosterStream login failed');
      },
    });
  };

  useEffect(() => {
    if (autologin && provider) {
      handleLogin(provider);
    } else {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('code')) {
        handleCodeLogin();
      }
      if (searchParams.get('error')) {
        handleError(searchParams.get('error') || 'RosterStream login failed');
        searchParams.delete('error');
      }

      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${searchParams.toString()}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autologin, provider]);

  return handleLogin;
}
